import { type ReactElement } from 'react'
import { focusLoadRow } from '@/utils/focus-load-row'

/** How one agent reply is rendered. Every option is off unless the caller turns it on. */
export interface AgentMarkdownOptions {
    /**
     * `#123` becomes a button that scrolls the AI Hub results table to load row 123 (`focusLoadRow`). Only for a
     * surface that shows that table; elsewhere `#123` stays plain text.
     */
    loadRefJump?: boolean
    /**
     * Markdown links and bare http(s) URLs open in a new tab. Only for the agent's own reply: tool output, broker
     * emails and load posts are third-party text, where a label can hide where a link goes. Off, they stay as
     * written, with the URL in view.
     */
    links?: boolean
}

// Triple-asterisk = bold + italic (`***text***`). Checked before bold/italic so the extra stars
// aren't left as literals (`**` alone would match and render `*text*` bold with stray asterisks).
const BOLD_ITALIC_REGEX = /\*\*\*(.+?)\*\*\*/g
const BOLD_REGEX = /\*\*(.+?)\*\*/g
// Single-asterisk emphasis (italic). CommonMark-ish: opening `*` not preceded by `*`/word char
// and not followed by space; closing `*` not preceded by space and not followed by `*`. This keeps
// `**bold**` (handled above, wins by earliest index), `a*b`, and `2 * 3` from matching.
const ITALIC_REGEX = /(?<![*\w])\*(?!\s)([^*\n]+?)(?<!\s)\*(?!\*)/g
const INLINE_CODE_REGEX = /`([^`]+)`/g
// Load reference (`#123`) — the agent's row numbers. Not preceded by a word char/`#`
// (skips anchors like `a#1` and `##`), not followed by more digits or `%`.
const LOAD_REF_REGEX = /(?<![\w#])#(\d{1,5})(?![\d%])/g
// `![alt](url "title")` — never loaded: a reply can carry text from tool data, and an <img> would fetch an outside
// URL with no click. Only its alt text is shown.
const IMAGE_REGEX = /!\[([^\]]*)\]\(((?:[^()\s]|\([^()\s]*\))*)(?:\s+"[^"]*")?\)/g
// `[text](url "title")`; the URL may hold one level of balanced parens (`…/Foo_(bar)`, `javascript:alert(1)`).
const LINK_REGEX = /\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)(?:\s+"[^"]*")?\)/g
// `<https://…>` or a bare `https://…`. Trailing punctuation is trimmed off in `bareUrlLength`.
const AUTOLINK_REGEX = /<(https?:\/\/[^\s<>]+)>|(?<![\w/])https?:\/\/[^\s<>]+/g
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?'"*_]+$/

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:'])

/** The href when it is an absolute http(s) or mailto URL, else null (so `javascript:`, `data:`, relative paths stay text).
 *  Any link a tool result carries goes through it, not only the Markdown's. */
export const safeHref = (raw: string): string | null => {
    try {
        const url = new URL(raw)
        return SAFE_PROTOCOLS.has(url.protocol) ? url.href : null
    } catch {
        return null
    }
}

/** A bare URL's real length: sentence punctuation and an unbalanced closing paren after it are not part of it. */
const bareUrlLength = (url: string): number => {
    let end = url.replace(TRAILING_PUNCTUATION_REGEX, '')
    while (end.endsWith(')') && (end.match(/\)/g) ?? []).length > (end.match(/\(/g) ?? []).length) {
        end = end.slice(0, -1).replace(TRAILING_PUNCTUATION_REGEX, '')
    }
    return end.length
}

type InlineType = 'bolditalic' | 'bold' | 'code' | 'image' | 'link' | 'autolink' | 'italic' | 'loadref'

interface InlineContext {
    options: AgentMarkdownOptions
    /** False inside a link's label, so a link never nests in another. */
    links: boolean
}

const LINK_CLASS = 'font-medium text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-700 dark:hover:text-blue-300 break-words'

const renderAnchor = (href: string, children: ReactElement | string, key: string): ReactElement => (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
        {children}
    </a>
)

const renderInlineWith = (text: string, key: string, ctx: InlineContext): ReactElement => {
    const parts: (string | ReactElement)[] = []
    let remaining = text
    let partIdx = 0

    while (remaining.length > 0) {
        // Earliest match wins. On the same index, `pri` breaks the tie so `***` wins over `**` wins over `*`
        // (else the extra stars leak as literals), and `![` wins over `[`.
        const regexes: { re: RegExp; type: InlineType; pri: number; on: boolean }[] = [
            { re: BOLD_ITALIC_REGEX, type: 'bolditalic', pri: 0, on: true },
            { re: BOLD_REGEX, type: 'bold', pri: 1, on: true },
            { re: INLINE_CODE_REGEX, type: 'code', pri: 2, on: true },
            { re: IMAGE_REGEX, type: 'image', pri: 3, on: true },
            { re: LINK_REGEX, type: 'link', pri: 4, on: ctx.links },
            { re: AUTOLINK_REGEX, type: 'autolink', pri: 5, on: ctx.links },
            { re: ITALIC_REGEX, type: 'italic', pri: 6, on: true },
            { re: LOAD_REF_REGEX, type: 'loadref', pri: 7, on: ctx.options.loadRefJump === true },
        ]
        const candidates = regexes
            .filter((r) => r.on)
            .map(({ re, type, pri }) => {
                re.lastIndex = 0
                return { m: re.exec(remaining), type, pri }
            })
            .filter((c): c is { m: RegExpExecArray; type: InlineType; pri: number } => c.m !== null)

        if (candidates.length === 0) {
            parts.push(remaining)
            break
        }

        candidates.sort((a, b) => a.m.index - b.m.index || a.pri - b.pri)
        const { m: earliestMatch, type: matchType } = candidates[0]

        if (earliestMatch.index > 0) {
            parts.push(remaining.slice(0, earliestMatch.index))
        }

        const childKey = `${key}-${partIdx}`
        let consumed = earliestMatch[0].length
        if (matchType === 'bolditalic') {
            // Recurse so nested emphasis inside resolves too.
            parts.push(
                <strong key={childKey} className="font-semibold">
                    <em className="italic">{renderInlineWith(earliestMatch[1], childKey, ctx)}</em>
                </strong>
            )
        } else if (matchType === 'bold') {
            parts.push(
                <strong key={childKey} className="font-semibold">
                    {renderInlineWith(earliestMatch[1], childKey, ctx)}
                </strong>
            )
        } else if (matchType === 'code') {
            // Code content is literal — no recursion.
            parts.push(
                <code key={childKey} className="px-1 py-0.5 rounded bg-gray-100 dark:bg-white/10 font-mono text-[0.85em]">
                    {earliestMatch[1]}
                </code>
            )
        } else if (matchType === 'image') {
            if (earliestMatch[1]) parts.push(earliestMatch[1])
        } else if (matchType === 'link') {
            const label = renderInlineWith(earliestMatch[1], childKey, { ...ctx, links: false })
            const href = safeHref(earliestMatch[2])
            parts.push(href ? renderAnchor(href, label, childKey) : label)
        } else if (matchType === 'autolink') {
            const bracketed = earliestMatch[1]
            const url = bracketed ?? earliestMatch[0].slice(0, bareUrlLength(earliestMatch[0]))
            if (!bracketed) consumed = url.length
            const href = safeHref(url)
            parts.push(href ? renderAnchor(href, url, childKey) : url)
        } else if (matchType === 'loadref') {
            // "#123" = the agent's load row number. Click scrolls the results
            // table to that row and flashes the highlight.
            const rowNumber = Number(earliestMatch[1])
            parts.push(
                <button
                    key={childKey}
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        focusLoadRow(rowNumber)
                    }}
                    title={`Show load #${rowNumber} in the table`}
                    className="inline font-medium text-blue-600 dark:text-blue-400 hover:underline underline-offset-2 cursor-pointer bg-transparent p-0 border-0"
                >
                    #{rowNumber}
                </button>
            )
        } else {
            parts.push(
                <em key={childKey} className="italic">
                    {renderInlineWith(earliestMatch[1], childKey, ctx)}
                </em>
            )
        }

        remaining = remaining.slice(earliestMatch.index + consumed)
        partIdx++
    }

    return <span key={key}>{parts}</span>
}

/** One line of agent Markdown: emphasis, inline code, image alt text and (opt-in) links and `#N` load jumps. Never raw HTML. */
export const renderAgentInline = (text: string, key: string, options: AgentMarkdownOptions): ReactElement =>
    renderInlineWith(text, key, { options, links: options.links === true })
